<template>
    <div class="event-card" :class="{ 'event-card--readonly': readonly }" @click="$emit('click')">
        <div class="event-thumb">
            <div v-if="avatar" class="event-thumb-img" :style="{ backgroundImage: `url('${avatar}')` }" />
            <div v-else class="event-thumb-placeholder" :style="gradientStyle">{{ initials }}</div>
        </div>
        <div class="event-inner">
            <div class="event-body">
                <div class="event-title">{{ title }}</div>
                <div class="event-meta">{{ meta }}</div>
                <div class="event-assignee">{{ $t('routines.assigned_to', { name: studentNameString }) }}</div>
            </div>
            <div v-if="!readonly" class="event-menu-wrap" @click.stop>
                <v-menu offset-y left min-width="170">
                    <template #activator="{ on, attrs }">
                        <v-btn icon x-small class="event-menu-btn" v-bind="attrs" v-on="on">
                            <v-icon size="14">mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>
                    <v-list dense class="event-ctx-menu">
                        <v-list-item link @click="$emit('click:edit')">
                            <v-icon size="14" class="mr-2">mdi-pencil-outline</v-icon>
                            <v-list-item-title>{{ $t('routines.edit_routine') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item link @click="$emit('click:copy')">
                            <v-icon size="14" class="mr-2">mdi-content-copy</v-icon>
                            <v-list-item-title>{{ $t('routines.copy_routine') }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item link @click="$emit('click:delete')">
                            <v-icon size="14" class="mr-2" color="error">$delete</v-icon>
                            <v-list-item-title class="error--text">{{ $t('routines.delete_routine') }}</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>
    </div>
</template>

<script>
import { getInitials, getAvatarGradient } from "@/utils/Common";

export default {
    name: "AgendaEventCard",
    props: {
        routine: {
            type: Object,
            default: null,
        },
        studentName: {
            type: String,
            default: null,
        },
        avatar: {
            type: String,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        studentNameString(vm = this) {
            return vm.studentName || '...'
        },
        title(vm = this) {
            return vm.routine?.name || vm.routine?.versus || '...'
        },
        isCompetence(vm = this) {
            return vm.routine?.type === 'competence'
        },
        initials(vm = this) {
            return getInitials(vm.studentName)
        },
        gradientStyle(vm = this) {
            const g = getAvatarGradient(vm.routine?.studentId || vm.studentName || '')
            return { background: g.background, color: g.color }
        },
        blocksQuantity(vm = this) {
            return vm.routine?.blocksQuantity || vm.routine?.blocks?.length || 0
        },
        minutes(vm = this) {
            if (vm.routine?.minutes) return Number(vm.routine.minutes)
            let minutes = 0
            vm.routine?.blocks?.forEach(block => {
                block.exercises.forEach(exercise => {
                    minutes += exercise?.config?.sets?.length || block.sets || 0
                })
            })
            return minutes
        },
        durationText(vm = this) {
            if (!vm.minutes) return vm.$t('common.not_available_short')
            const minutesString = vm.$tc('common.minutes', vm.minutes === 1 ? 1 : 2).toLowerCase()
            const minutesAdded = Number(vm.minutes * 1.3).toFixed()
            if (vm.minutes === Number(minutesAdded)) return `${vm.minutes} ${minutesString}`
            return `${vm.minutes} - ${minutesAdded} ${minutesString}`
        },
        meta(vm = this) {
            if (vm.isCompetence) return vm.$t('common.competence')
            const blocksString = vm.$tc('common.blocks', 2).toLowerCase()
            return `${vm.blocksQuantity} ${blocksString} · ${vm.durationText}`
        },
    },
}
</script>

<style scoped>
.event-card {
    display: flex;
    align-items: stretch;
    border: 1px solid var(--v-outline-base);
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.12s;
    height: 72px;
}

.event-card:hover {
    border-color: var(--v-outline2-base);
}

.event-card--readonly {
    cursor: default;
}

.event-card--readonly:hover {
    border-color: var(--v-outline-base);
}

.event-thumb {
    width: 52px;
    flex-shrink: 0;
    background: var(--v-surface2-base);
    overflow: hidden;
    position: relative;
}

.event-thumb-img {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: 50% 50%;
}

.event-thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.03em;
}

.event-inner {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px 8px 12px;
}

.event-body {
    flex: 1;
    min-width: 0;
}

.event-title {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.event-meta {
    font-size: 11px;
    line-height: 1.3;
    color: var(--v-txtSecondary-base);
    margin-top: 2px;
}

.event-assignee {
    font-size: 11px;
    line-height: 1.3;
    color: var(--v-txtMuted-base);
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.event-menu-wrap {
    flex-shrink: 0;
}

.event-menu-btn {
    opacity: 0;
    transition: opacity 0.15s;
}

.event-card:hover .event-menu-btn {
    opacity: 1;
}

.event-menu-btn >>> .v-icon {
    color: var(--v-txtMuted-base);
}

.event-menu-btn:hover >>> .v-icon {
    color: var(--v-txtSecondary-base);
}

@media (max-width: 960px) {
    .event-menu-btn {
        opacity: 1;
    }
}
</style>
